/**
 * LandingPageContainer
 *
 * This component receives the summary product data from /api/nbamarketplace/products/ endpoint, as stored in redux state, and
 * displays these in a variety of layout styles as the marketplace landing page.
 *
 * It is this component that defines the layout, and not any of the product data.
 *
 * The data comes from the initial 'Header' component for each product in MS Dynamics.
 * Even though a CTA field is defined in the 'Header' component, it is not used here, and instead the product ID is used
 * to redirect to the product pages.
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';

import { marketplaceProductListRequest } from '../../store/marketplace/marketplace-actions';
import { getMarketplaceProductListSelector } from '../../store/marketplace/marketplace-selectors';

import Tiles from '../../components/Tiles';

import { LandingPageHeader } from './components/LandingPageHeader';
import { EventsManager, usePayload, Config } from '@vm/omega-shared-component-webapp';

import { HEADER_TITLE, HEADER_DESCRIPTION, HEADER_IMAGE, TILES_TP_TITLE, TILES_TP_DESCRIPTION, TILES_VM_TITLE, MP_LANDING_PAGE_CONTAINER_DATA_TESTID } from './constants';

import { ComponentType, ComponentStyle, ProductCategory, ProductComponent, ProductListItem, RGBColorType } from '../../types/types';

type SplitLendingProductType = {
  lending: ProductListItem[];
  spotlight: ProductListItem[];
};

const Container = styled.div({
  backgroundColor: 'var(--color-light-grey)',
});

let splitLendingProducts: SplitLendingProductType;

/**
 * We are wanting to split the lending products, so a specific one has a panel to itself, so some custom
 * logic to separate the products out
 */
const splitProducts = (lendingProductData: ProductListItem[]): SplitLendingProductType => ({
  lending: lendingProductData.filter(lendingProduct => lendingProduct?.product?.metadata.title !== 'Foreign Currency Solutions'),
  spotlight: lendingProductData.filter(lendingProduct => lendingProduct?.product?.metadata.title === 'Foreign Currency Solutions'),
});

/**
 * Because we need to highlight one particular product, we are using specific ProductComponent data
 * for the highlight.
 */
export const generateSpotlightProductData = (spotlightData: ProductListItem) => {
  if (!spotlightData) return null;
  const { metadata, productId } = spotlightData?.product;
  const { image, title, description, cta } = metadata;

  const lendingProductCmponent: ProductComponent = {
    cta: {
      buttonText: cta.buttonText,
      cta: productId,
      isWhite: false,
    },
    description,
    image,
    style: ComponentStyle.E,
    title,
    type: ComponentType.CTA,
    palette: [
      { name: 'background', type: RGBColorType.SolidBackground, rgb1: '#D3E629', rgb2: '' },
      { name: 'text', type: RGBColorType.Text, rgb1: '#000000', rgb2: '' },
    ],
  };

  return lendingProductCmponent;
};

export const LandingPageContainer: React.FC = () => {
  const { payload, pushPayloadToHistory } = usePayload();

  const dispatch = useDispatch();
  const { data, ready, error } = useSelector(getMarketplaceProductListSelector);

  useEffect(() => {
    EventsManager.pageTitle.update('Marketplace');
  }, []);

  useEffect(() => {
    dispatch(marketplaceProductListRequest());
  }, [dispatch]);

  const handleProductClick = id => {
    pushPayloadToHistory({ ...payload, productids: [id] }, Config.get('paths.marketplace.product'));
  };

  const hasHeaderImage = Config.get('landingPage.header.image', false);

  if (error) {
    EventsManager.systemError.publishCriticalError({ error: error });
  }

  if (ready && data[ProductCategory.LENDING]) {
    splitLendingProducts = splitProducts(data[ProductCategory.LENDING]);
  }

  /**
   * Only the Carousel and Panel Components are used by the landing page exclusively
   * Note: Legacy implementation - The CTA text for the product summary from MSD is overridden here.
   *       The CTA click is handler is passed down via props so all redirection on CTAs is handled locally here.
   */

  return ready && splitLendingProducts?.lending && splitLendingProducts?.spotlight ? (
    <Container data-testid={MP_LANDING_PAGE_CONTAINER_DATA_TESTID}>
      <LandingPageHeader title={HEADER_TITLE} description={HEADER_DESCRIPTION} image={hasHeaderImage && HEADER_IMAGE} />

      <Tiles style={ComponentStyle.B} id='tiles-tp' title={TILES_TP_TITLE} description={TILES_TP_DESCRIPTION} tileItems={data[ProductCategory.THIRD_PARTY]} handleProductClick={handleProductClick} backgroundPattern={false} />
      <Tiles style={ComponentStyle.A} id='tiles-vm' title={TILES_VM_TITLE} tileItems={data[ProductCategory.LENDING].concat(data[ProductCategory.SAVINGS])} handleProductClick={handleProductClick} backgroundPattern={true} />
    </Container>
  ) : null;
};
